import { doSignalingHandshake } from './net/signaling.js'
import { openGameSocket } from './net/websocket.js'
import { decodeViewscreen, ViewscreenPacketBytes } from './wire/viewscreen.js'
import { decodeProjectilePacket } from './wire/projectile_packet.js'
import { isShipDetail } from './wire/ship_detail.js'
import { KeyboardInput } from './input/keyboard.js'
import { GameState } from './game/game.js'
import { ProjectileSet } from './game/projectiles.js'
import { render } from './render/canvas.js'
import { pointInButton } from './render/arena_lobby.js'
import { log, configureLog, setLogLevel } from './log.js'
import { configureTestHandlers, handleTestCommand } from './test_handlers.js'
import {
  GameMode,
  getGameMode,
  configureModeBridge,
  applyServerMode,
  requestMode,
  onModeChange,
} from './mode/state.js'

// Wire-level FrameKind values (must match backend/node/game_transport.go).
const FrameKindViewscreenUpdate = 2
const FrameKindProjectileUpdate = 3

const statusEl = document.getElementById('status')
const canvas = document.getElementById('game')

function resizeCanvas() {
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
}
resizeCanvas()
window.addEventListener('resize', resizeCanvas)

async function main() {
  statusEl.textContent = 'connecting…'
  let pc
  let dc
  let gameID
  try {
    const r = await doSignalingHandshake()
    pc = r.pc
    dc = r.dc
    gameID = r.resp.gameID
    statusEl.textContent = `connected · game ${gameID} · waiting for lobby`
  } catch (e) {
    statusEl.textContent = `connect failed: ${e}`
    return
  }

  const state = new GameState()
  const projectiles = new ProjectileSet()
  const input = new KeyboardInput()
  input.start()

  // Iter 1.14: keep the input layer's mode in sync with the mode store so
  // snapshot() returns all-zeroes outside of arena.
  onModeChange((mode) => {
    input.setGameMode(mode)
    statusEl.textContent = `connected · game ${gameID} · ${mode}`
  })

  // Open WebSocket for ship_detail binding messages and server-pushed
  // control frames (iter 1.10: `set_log_mode`; iter 1.12: `test.*`;
  // iter 1.14: `set_game_mode`).
  const ws = openGameSocket(gameID, (obj) => {
    if (obj && obj.kind === 'set_game_mode' && typeof obj.mode === 'string') {
      applyServerMode(obj.mode, obj.seq | 0)
      return
    }
    if (obj && obj.kind === 'set_log_mode' && typeof obj.level === 'string') {
      setLogLevel(obj.level)
      log.info('log level changed', { level: obj.level })
      return
    }
    if (obj && typeof obj.kind === 'string' && obj.kind.startsWith('test.')) {
      handleTestCommand(obj)
      return
    }
    if (obj && obj.kind === 'projectile_stop') {
      projectiles.applyStop(obj)
      return
    }
    if (isShipDetail(obj)) {
      // Iter 1.14: the spawn flow emits ship_detail BEFORE the
      // set_game_mode=arena push, so the ship_detail naturally arrives
      // while we're still in arena_lobby. Ingest it regardless of mode;
      // the renderer is the mode router that decides what to draw.
      const wasInit = state.self !== null
      state.ingestShipDetail(obj)
      if (!wasInit && state.self) {
        statusEl.textContent = `connected · game ${gameID} · ship ${state.self.shipID}`
        log.info('ship_bound', { gameID, shipID: state.self.shipID })
      }
    }
  })
  configureModeBridge(ws)

  // Lobby Start-button click handler. Idempotent: outside arena_lobby it's
  // a no-op; inside arena_lobby it triggers a request_game_mode = arena.
  canvas.addEventListener('click', (ev) => {
    if (getGameMode() !== GameMode.ARENA_LOBBY) return
    const rect = canvas.getBoundingClientRect()
    const x = ev.clientX - rect.left
    const y = ev.clientY - rect.top
    if (pointInButton(canvas, x, y)) {
      requestMode('arena')
    }
  })

  // Production default is 'error' — errors flow even when no one is
  // actively debugging. Server can raise verbosity via `set_log_mode`.
  // Iter 1.12: an `initial_log_level` URL param can pre-set the threshold
  // at boot, used by headless tests so events emitted before the first
  // SetGameLogMode arrives (e.g. ship_bound) aren't filtered out.
  const initParams = new URLSearchParams(window.location.search)
  const initialLevel = initParams.get('initial_log_level') || 'error'
  configureLog({ socket: ws, defaultLevel: initialLevel })
  configureTestHandlers({ state, projectiles, input, canvas })

  dc.onmessage = (ev) => {
    if (!(ev.data instanceof ArrayBuffer)) return
    if (ev.data.byteLength < 1) return
    const u8 = new Uint8Array(ev.data)
    const kind = u8[0]
    const body = ev.data.slice(1)
    switch (kind) {
      case FrameKindViewscreenUpdate: {
        if (body.byteLength !== ViewscreenPacketBytes) return
        const pkt = decodeViewscreen(body)
        if (!pkt) return
        state.ingestPacket(pkt.header, pkt.records)
        break
      }
      case FrameKindProjectileUpdate: {
        const pkt = decodeProjectilePacket(body)
        if (!pkt) return
        state.noteServerTick(pkt.header.serverTick)
        for (const r of pkt.records) {
          projectiles.ingest(pkt.header, r)
        }
        break
      }
      default:
        break
    }
  }
  dc.onopen = () => {
    statusEl.textContent = (statusEl.textContent ?? '') + ' · dc open'
  }
  dc.onclose = () => {
    statusEl.textContent = (statusEl.textContent ?? '') + ' · dc closed'
  }

  pc.addEventListener('connectionstatechange', () => {
    const base = (statusEl.textContent ?? '').split(' · pc=')[0]
    statusEl.textContent = `${base} · pc=${pc.connectionState}`
  })

  function frame() {
    const snapshot = input.snapshot()
    const kbState = {
      rotateLeftHeld: (snapshot.flags & 2) !== 0,
      rotateRightHeld: (snapshot.flags & 4) !== 0,
    }
    state.tick(kbState)
    projectiles.tick(state.viewTick)
    render({ canvas, self: state.self, ships: state.ships, projectiles })
    if (dc.readyState === 'open' && getGameMode() === GameMode.ARENA) {
      try {
        dc.send(input.encodedSnapshot())
      } catch {
        /* DC may be closing */
      }
    }
    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)

  void ws
}

main().catch((e) => {
  statusEl.textContent = `error: ${e}`
  console.error(e)
})
